import { useCallback, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { DoneButton } from "containers/Modal/styles";
import { setModal } from "state/config-slice";
import { offersState, selectOfferByName, selectOffersByPartner, selectOfferByDeepLink } from "state/offers-slice";
import Offer, { NotEligibleState, Status } from "types/Offer";

const useDeeplink = () => {
  const [cookies, , removeCookies] = useCookies();
  const offers = useSelector(offersState);
  const offersByPartner: Offer[] = useSelector(selectOffersByPartner(cookies.PARTNER));

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const activeOffersByPartner = offersByPartner.filter(
    (offer: Offer) => offer.marketplaceInfo.status === Status.active,
  );

  const purchasableOffersByPartner = offersByPartner.filter(
    (offer: Offer) => offer.marketplaceInfo.status === Status.purchasable,
  );

  const pendingActivationOffersByPartner = offersByPartner.filter(
    (offer: Offer) => offer.marketplaceInfo.status === Status.pendingActivation,
  );

  const restOffersByPartner = offersByPartner.filter(
    (offer: Offer) =>
      offer.marketplaceInfo.status !== Status.active &&
      offer.marketplaceInfo.status !== Status.purchasable &&
      offer.marketplaceInfo.status !== Status.pendingActivation,
  );

  const offersByPartnerSortedByPriority: Offer[] = [
    ...activeOffersByPartner,
    ...purchasableOffersByPartner,
    ...pendingActivationOffersByPartner,
    ...restOffersByPartner,
  ];

  const selectedOfferToDeeplinkWithPartner: Offer = offersByPartnerSortedByPriority[0];

  const offerByName: Offer = useSelector(selectOfferByName(cookies.OFFER));
  const offerByDeepLink: Offer = useSelector(selectOfferByDeepLink(cookies.OFFER));

  const selectedOfferToDeeplinkWithName = offerByName || offerByDeepLink;

  const openNotEligibleModal = useCallback(() => {
    const notEligible = selectedOfferToDeeplinkWithName?.marketplaceInfo?.notEligible;

    const state: NotEligibleState = cookies.STATE;
    const { heading, details } = notEligible?.[state] || {};

    dispatch(
      setModal({
        showModal: true,
        errorModal: true,
        size: "large",
        title: t(heading as string),
        description: t(details as string),
        customButtons: (
          <DoneButton
            title={t("Ok")}
            onClick={() => {
              dispatch(setModal({ showModal: false }));
              navigate("/", { state: { initial_deeplink: true } });
              removeCookies("STATE", { path: "/" });
              removeCookies("OFFER", { path: "/subscriptionplus" });
            }}
          />
        ),
      }),
    );
  }, [selectedOfferToDeeplinkWithName, removeCookies, cookies.STATE, navigate, dispatch, t]);

  const handleNotEligible = useCallback(
    (offer: Offer) => {
      const isModal = offer?.marketplaceInfo?.notEligible?.[cookies?.STATE as NotEligibleState]?.isModal;

      if (isModal) {
        // Do this if it's a SA or HB
        openNotEligibleModal();
      } else {
        // Do this if it's a trial
        navigate(`/offer/${offer.name}/not-eligible`, { state: { initial_deeplink: true } });
      }
    },
    [cookies.STATE, openNotEligibleModal, navigate],
  );

  useEffect(() => {
    if (offers.offersList.length > 0 && !cookies.STATE && cookies.OFFER && selectedOfferToDeeplinkWithName) {
      navigate(`/offer/${selectedOfferToDeeplinkWithName.name}`, { state: { initial_deeplink: true } });
      removeCookies("PARTNER", { path: "/subscriptionplus" });
      removeCookies("OFFER", { path: "/subscriptionplus" });
    } else if (
      offers.offersList.length > 0 &&
      !cookies.STATE &&
      !cookies.OFFER &&
      cookies.PARTNER &&
      selectedOfferToDeeplinkWithPartner
    ) {
      navigate(`/offer/${selectedOfferToDeeplinkWithPartner.name}`, { state: { initial_deeplink: true } });
      removeCookies("PARTNER", { path: "/subscriptionplus" });
      removeCookies("OFFER", { path: "/subscriptionplus" });
    }
  }, [
    selectedOfferToDeeplinkWithName,
    selectedOfferToDeeplinkWithPartner,
    navigate,
    removeCookies,
    offers.offersList.length,
    cookies.STATE,
    cookies.OFFER,
    cookies.PARTNER,
    cookies.ACTIVATIONCODE,
    dispatch,
  ]);

  useEffect(() => {
    if (offers.offersList.length === 0) return;

    const canHaveTrial = ![
      "not_eligible_for_trial",
      "account_already_exist",
      "access_denied",
      "youtube_partner_error",
    ].includes(cookies?.STATE);

    if (canHaveTrial) return;

    if (cookies.OFFER && selectedOfferToDeeplinkWithName) {
      handleNotEligible(selectedOfferToDeeplinkWithName);
    } else if (cookies.PARTNER && selectedOfferToDeeplinkWithPartner) {
      handleNotEligible(selectedOfferToDeeplinkWithPartner);
    }
  }, [
    handleNotEligible,
    selectedOfferToDeeplinkWithName,
    selectedOfferToDeeplinkWithPartner,
    dispatch,
    navigate,
    offers.offersList.length,
    cookies.STATE,
    cookies.OFFER,
    cookies.PARTNER,
  ]);

  return;
};

export default useDeeplink;
